import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { APIProvider } from '@vis.gl/react-google-maps';
import LoginPage from './components/public/LoginPage';
import PeopleDetail from './components/PeopleDetail';
import ErrorBoundary from './components/ErrorBoundary';
import { LanguageProvider } from './context/LanguageContext';
import { ThemeProvider } from './context/ThemeContext';
import { logout } from './services/authService';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

// Mobile drag and drop polyfill for touch devices
import { polyfill } from 'mobile-drag-drop';
import 'mobile-drag-drop/default.css';

// Initialize touch drag and drop polyfill
polyfill({
    dragImageTranslateOverride: (dragImage, translate, evt) => {
        // Scroll behavior during drag on mobile
        if (window.innerHeight - evt.clientY < 100) {
            window.scrollBy(0, 5);
        } else if (evt.clientY < 100) {
            window.scrollBy(0, -5);
        }
    }
});


// Public Components
import LandingPage from './components/public/LandingPage';
import DiscoverPage from './components/public/DiscoverPage';
import PricingPage from './components/public/PricingPage';
import RegisterPage from './components/public/RegisterPage';

// Legal Components
import LegalPage from './components/public/LegalPage';
import PrivacyPage from './components/public/PrivacyPage';
import TermsPage from './components/public/TermsPage';
import CookiesPage from './components/public/CookiesPage';

// Payment Components
import PaymentCallback from './components/payment/PaymentCallback';

// Dashboard Components (logged-in users)
import UserDashboard from './components/dashboard/UserDashboard';
import DashboardPeopleDetail from './components/dashboard/PeopleDetail';
import DashboardPeopleForm from './components/dashboard/DashboardPeopleForm';
import OccupationsPage from './components/dashboard/OccupationsPage';
import ContactDetail from './components/dashboard/ContactDetail';

// Check if registration is disabled via environment variable
const isRegistrationDisabled = import.meta.env.VITE_DISABLE_REGISTRATION === 'true';

function App() {
    const [language, setLanguage] = useState('fr');
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // Check for OAuth callback parameters on mount
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const authToken = params.get('auth_token');
        const authEmail = params.get('auth_email');
        const authError = params.get('auth_error');

        if (authToken && authEmail) {
            // OAuth login successful - store token and email first
            localStorage.setItem('authToken', authToken);
            localStorage.setItem('userEmail', authEmail);

            // Fetch full user data to get UUID, role, storageKey, etc.
            fetch(`https://apilinkoo.creago.be/rest/linkoo/peoples?email=${encodeURIComponent(authEmail)}`, {
                headers: { 'Accept': 'application/json' }
            })
            .then(res => res.json())
            .then(result => {
                const success = result.success || result.SUCCESS;
                const data = result.data || result.DATA || [];
                if (success && data.length > 0) {
                    const userData = data[0];
                    const firstName = userData.firstname || userData.firstName || '';
                    const lastName = userData.lastname || userData.lastName || '';
                    const userId = userData.id || '';
                    const userUuid = userData.peopleuuid || userData.peopleUuid || '';
                    const storageKey = userData.storagekey || userData.storageKey || '';
                    const userRole = userData.role || '';

                    localStorage.setItem('userName', `${firstName} ${lastName}`.trim());
                    localStorage.setItem('userId', userId.toString());
                    localStorage.setItem('userUuid', userUuid);
                    localStorage.setItem('userRole', userRole);
                    localStorage.setItem('linkoo_user', JSON.stringify({
                        id: userId,
                        email: authEmail,
                        firstName: firstName,
                        lastName: lastName,
                        peopleUuid: userUuid,
                        storageKey: storageKey,
                        role: userRole
                    }));
                    console.log('OAuth user data loaded:', { userUuid, userRole });
                }
            })
            .catch(err => console.error('Failed to fetch user data after OAuth:', err));

            setIsAuthenticated(true);
            window.history.replaceState({}, document.title, window.location.pathname);
            alert(language === 'fr' ? 'Connexion réussie avec Google!' : 'Successfully logged in with Google!');
        } else if (authError) {
            // OAuth login failed
            let errorMessage = language === 'fr' ? 'Erreur lors de la connexion: ' : 'Login error: ';
            // ... (keep existing error handling)
            switch (authError) {
                case 'email_not_verified':
                    errorMessage += language === 'fr' ? 'Email non vérifié' : 'Email not verified';
                    break;
                case 'token_exchange_failed':
                    errorMessage += language === 'fr' ? 'Échec de l\'échange de token' : 'Token exchange failed';
                    break;
                case 'userinfo_failed':
                    errorMessage += language === 'fr' ? 'Impossible de récupérer les informations utilisateur' : 'Failed to retrieve user information';
                    break;
                default:
                    errorMessage += authError;
            }
            alert(errorMessage);
            window.history.replaceState({}, document.title, window.location.pathname);
        }

        // Check if already authenticated
        const token = localStorage.getItem('authToken');
        if (token) {
            setIsAuthenticated(true);
        }
    }, [language]);

    const handleLogout = async () => {
        // Save contacts to server before logout
        await logout();
        // Clear remaining auth data
        localStorage.removeItem('authToken');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userName');
        localStorage.removeItem('userId');
        localStorage.removeItem('userUuid');
        localStorage.removeItem('userRole');
        localStorage.removeItem('linkoo_user');
        localStorage.removeItem('linkoo_user_contacts');
        setIsAuthenticated(false);
    };

    return (
        <ErrorBoundary>
            <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={['marker', 'places']}>
                <LanguageProvider>
                    <ThemeProvider>
                        <Router>
                        <title>Linkoo</title>
                        <Routes>
                            {/* Public Routes */}
                            <Route path="/discover" element={<DiscoverPage onLogin={() => window.location.href = '/login'} />} />
                            <Route path="/pricing" element={<PricingPage onLogin={() => window.location.href = '/login'} />} />
                            <Route path="/tarification" element={<PricingPage onLogin={() => window.location.href = '/login'} />} />
                            <Route path="/payment/callback" element={<PaymentCallback onLogin={() => window.location.href = '/login'} />} />

                            {/* Legal Routes */}
                            <Route path="/legal" element={<LegalPage />} />
                            <Route path="/privacy" element={<PrivacyPage />} />
                            <Route path="/terms" element={<TermsPage />} />
                            <Route path="/cookies" element={<CookiesPage />} />

                            <Route path="/login" element={
                                !isAuthenticated ? (
                                    <LoginPage
                                        onBack={() => window.history.back()}
                                        language={language}
                                        initialMode="login"
                                    />
                                ) : <Navigate to="/" replace />
                            } />
                            <Route path="/register" element={
                                isRegistrationDisabled ? (
                                    <Navigate to="/login" replace />
                                ) : !isAuthenticated ? (
                                    <RegisterPage onLogin={() => window.location.href = '/login'} />
                                ) : <Navigate to="/" replace />
                            } />

                            {/* Main Route Handling */}
                            <Route
                                path="/"
                                element={
                                    !isAuthenticated ? (
                                        <LandingPage onLogin={() => window.location.href = '/login'} />
                                    ) : (
                                        <UserDashboard />
                                    )
                                }
                            />

                            <Route
                                path="/people/:uuid"
                                element={
                                    isAuthenticated ? (
                                        <DashboardPeopleDetail onLogout={handleLogout} />
                                    ) : (
                                        <PeopleDetail />
                                    )
                                }
                            />

                            {/* Private contact detail route - authenticated only */}
                            <Route
                                path="/contact/:localuuid"
                                element={
                                    isAuthenticated ? (
                                        <ContactDetail onLogout={handleLogout} />
                                    ) : (
                                        <Navigate to="/login" replace />
                                    )
                                }
                            />

                            {/* Add contact route - authenticated only */}
                            <Route
                                path="/add-contact"
                                element={
                                    isAuthenticated ? (
                                        <DashboardPeopleForm onLogout={handleLogout} />
                                    ) : (
                                        <Navigate to="/login" replace />
                                    )
                                }
                            />

                            {/* Occupations management - authenticated only */}
                            <Route
                                path="/occupations"
                                element={
                                    isAuthenticated ? (
                                        <OccupationsPage />
                                    ) : (
                                        <Navigate to="/login" replace />
                                    )
                                }
                            />

                            {/* Catch all - redirect to home */}
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                        </Router>
                    </ThemeProvider>
                </LanguageProvider>
            </APIProvider>
        </ErrorBoundary>
    );
}

export default App;
